import Component from '@glimmer/component';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { PhSun, PhMoon, PhCloudSun, PhGraduationCap } from 'ember-phosphor-icons';
import BetaHomeAlertRow from 'tio-employee/components/beta/home/alert-row';
import BetaHomeDiscoverCard from 'tio-employee/components/beta/home/discover-card';
import BetaHomeProgressCard from 'tio-employee/components/beta/home/progress-card';
import BetaHomeSiteFooter from 'tio-employee/components/beta/home/site-footer';
import BetaHomeStatCard from 'tio-employee/components/beta/home/stat-card';
import { buildBetaHomeModel } from 'tio-employee/utils/beta/home-model';
import { betaHomeFlags } from 'tio-employee/utils/beta/home-flags';
import type { BetaHomeFlags, BetaHomeModel } from 'tio-employee/utils/beta/home-model';
import type BetaBrandService from 'tio-employee/services/beta-brand';
import type PartnerService from 'tio-employee/services/partner';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type SessionContextService from 'tio-employee/services/session-context';
import type { BetaHomeRouteModel } from 'tio-employee/routes/authenticated/dashboard';

export interface BetaHomeSignature {
  Args: {
    model: BetaHomeRouteModel;
  };
  Element: HTMLDivElement;
}

/**
 * Beta home page — Brand Updates 2026.
 *
 * Design source: Claude Design project 9190654a-6db9-428c-b7ef-5cbb8d5ee149,
 * file `Home Page Concepts.dc.html`.
 *
 * Renders one of the design's two user states. All field mapping lives in
 * `utils/beta/home-model.ts`; this component only supplies services-derived
 * inputs and lays the sections out.
 *
 * NOT YET IN SCOPE (see BETA_HOME_GAPS.md): the design also restyles the
 * sidebar and the top-level page chrome. Those are app-shell changes affecting
 * every route, so they are a separate change — this component renders inside
 * the existing chrome.
 */
export default class BetaHome extends Component<BetaHomeSignature> {
  @service declare sessionContext: SessionContextService;
  @service declare partner: PartnerService;
  @service declare reimaginedData: ReimaginedDataService;
  @service('beta-brand') declare betaBrand: BetaBrandService;

  get firstName(): string {
    return (
      this.sessionContext.user?.person?.firstName ||
      this.sessionContext.currentEmployee?.firstName ||
      ''
    );
  }

  get flags(): BetaHomeFlags {
    return betaHomeFlags(this.partner, this.reimaginedData);
  }

  get home(): BetaHomeModel {
    return buildBetaHomeModel({
      model: this.args.model.reimagined,
      firstName: this.firstName,
      hour: new Date().getHours(),
      actionItems: this.args.model.actionItems,
      flags: this.flags,
      tuitionYearToDate: this.args.model.tuitionYearToDate,
    });
  }

  get greetingIcon() {
    const hour = new Date().getHours();
    if (hour < 12) return PhSun;
    if (hour < 17) return PhCloudSun;
    return PhMoon;
  }

  /** The design's section heading changes with the user state. */
  get discoverHeading(): string {
    return this.home.audience === 'new' ? 'Explore Your Benefits' : 'More to Explore';
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <div
      class="tio-beta-home"
      data-beta-brand={{this.betaBrand.brand}}
      data-test-beta-home={{this.home.audience}}
      ...attributes
    >
      <div class="tio-beta-home__main">
        {{! The design has no page-header band — the hero carries the visible
            heading treatment — so the route's semantic h1 is screen-reader only,
            exactly as the legacy dashboard branch below does it. Uses the
            existing translated key rather than hardcoding English, since this one
            string is already localized in all three locales. }}
        <h1 class="sr-only">{{t "sidebar.home"}}</h1>

        {{! ── Hero ──────────────────────────────────────────────────── }}
        <section class="tio-beta-home__section">
          {{#if (eq this.home.audience "new")}}
            <div class="tio-beta-home__hero tio-beta-home__hero--new">
              <div class="tio-beta-home__hero-copy">
                <div class="tio-col-tight">
                  <div class="tio-beta-home__greeting">
                    {{#let this.greetingIcon as |GreetingIcon|}}
                      <GreetingIcon @size={{20}} />
                    {{/let}}
                    <span>{{this.home.greeting}}, {{this.home.firstName}}</span>
                  </div>
                  <p class="tio-beta-home__title">Welcome To Your Education Benefits</p>
                </div>

                {{! The design's lede is TA-specific. A partner without TA would
                    otherwise be welcomed to a benefit they do not offer. }}
                {{#if this.flags.showTuitionAssistance}}
                  <p class="tio-beta-home__lede">
                    Whether you’re mastering a new skill or earning a new credential, Tuition
                    Assistance is here to help cover the costs.
                  </p>
                {{else}}
                  <p class="tio-beta-home__lede">
                    Your employer has education benefits waiting for you. Take a look at what’s
                    available.
                  </p>
                {{/if}}

                <a href="#beta-home-explore" class="tio-beta-home__cta">Learn More</a>
              </div>

              {{#if this.flags.showTuitionAssistance}}
                <div class="tio-beta-home__hero-aside">
                  <div class="tio-col">
                    <div class="tio-beta-home__hero-aside-label">Available Tuition Assistance</div>
                    <div
                      class="tio-beta-home__hero-aside-value"
                    >{{this.home.availableTuition}}</div>
                  </div>
                  <div class="tio-beta-home__meta">
                    <span>grow your skills for less</span>
                    <PhGraduationCap @size={{16}} />
                  </div>
                </div>
              {{/if}}
            </div>
          {{else}}
            <div class="tio-beta-home__hero tio-beta-home__hero--active">
              <div class="tio-beta-home__hero-head">
                <div class="tio-col-tight">
                  <div class="tio-beta-home__greeting">
                    {{#let this.greetingIcon as |GreetingIcon|}}
                      <GreetingIcon @size={{20}} />
                    {{/let}}
                    <span>{{this.home.greeting}}, {{this.home.firstName}}</span>
                  </div>
                  <p class="tio-beta-home__title">Your Education Benefits Dashboard</p>
                </div>

                <a href="#beta-home-explore" class="tio-beta-home__cta">View Benefits</a>
              </div>

              <div class="tio-beta-home__grid tio-beta-home__grid--quad">
                {{#each this.home.stats as |stat|}}
                  <BetaHomeStatCard @stat={{stat}} />
                {{/each}}
              </div>
            </div>
          {{/if}}
        </section>

        {{! ── Needs Your Attention ──────────────────────────────────── }}
        {{#if this.home.alerts.length}}
          <section class="tio-beta-home__section tio-beta-home__section--attention">
            <div class="tio-beta-home__attention">
              <div class="tio-beta-home__attention-head">
                <h2 class="tio-beta-home__section-title">Needs Your Attention</h2>
                <span class="tio-beta-home__attention-count">({{this.home.alerts.length}})</span>
              </div>

              <div class="tio-col-tight">
                {{#each this.home.alerts as |alert|}}
                  <BetaHomeAlertRow @alert={{alert}} />
                {{/each}}
              </div>
            </div>
          </section>
        {{/if}}

        {{! ── Active Benefits ───────────────────────────────────────── }}
        {{#if this.home.progressCards.length}}
          <section class="tio-beta-home__section">
            <h2 class="tio-beta-home__section-title">Active Benefits</h2>
            <div class="tio-beta-home__grid">
              {{#each this.home.progressCards as |card|}}
                <BetaHomeProgressCard @card={{card}} />
              {{/each}}
            </div>
          </section>
        {{/if}}

        {{! ── Explore Your Benefits / More to Explore ───────────────── }}
        {{#if this.home.discover.length}}
          <section id="beta-home-explore" class="tio-beta-home__section">
            <h2 class="tio-beta-home__section-title">{{this.discoverHeading}}</h2>
            <div class="tio-beta-home__grid tio-beta-home__grid--quad">
              {{#each this.home.discover as |card|}}
                <BetaHomeDiscoverCard @card={{card}} />
              {{/each}}
            </div>
          </section>
        {{/if}}

        {{! ── Resources ─────────────────────────────────────────────── }}
        <section class="tio-beta-home__section tio-beta-home__section--last">
          <h2 class="tio-beta-home__section-title">Resources</h2>
          <div class="tio-beta-home__grid tio-beta-home__grid--quad">
            {{#each this.home.resources as |card|}}
              <BetaHomeDiscoverCard @card={{card}} />
            {{/each}}
          </div>
        </section>
      </div>

      <BetaHomeSiteFooter />
    </div>
  </template>
}
